import type { CameraState, RenderObject } from '@v2/contracts'
import { add3, invert4, lookAt4, multiply4, normalize3, orthographic4, perspective4, scale3, type Mat4, type Vec3 } from '@v2/math'

export interface CameraMatrices {
  eye: Vec3
  viewProjection: Mat4
  inverseViewProjection: Mat4
  lightViewProjection: Mat4
  uniforms: Float32Array
}

/** Camera/lighting transforms have no access to games, actions, or DOM state. */
export function buildCamera(camera: CameraState, width: number, height: number, objects: readonly RenderObject[]): CameraMatrices {
  const horizontal = Math.cos(camera.elevation) * camera.distance
  const eye = add3(camera.target, [Math.sin(camera.azimuth) * horizontal, Math.sin(camera.elevation) * camera.distance, Math.cos(camera.azimuth) * horizontal])
  const projection = perspective4(camera.fov, Math.max(0.001, width / height), 0.1, 200)
  const viewProjection = multiply4(projection, lookAt4(eye, camera.target))
  // Fit a stable world-aligned light frustum around the resolved table. A small
  // quantization prevents tiny animations from continually shifting shadow texels.
  let extent = 8
  for (const object of objects) {
    if (object.layer === 'foreground') continue
    extent = Math.max(extent, Math.abs(object.position[0]) + Math.hypot(object.size[0], object.size[2]) / 2, Math.abs(object.position[2]) + Math.hypot(object.size[0], object.size[2]) / 2)
  }
  extent = Math.ceil(extent / 2) * 2
  const light = normalize3([-0.65, 1.3, 0.65])
  const lightEye = scale3(light, extent * 2.5)
  const lightViewProjection = multiply4(orthographic4(-extent, extent, -extent, extent, 0.1, extent * 5), lookAt4(lightEye, [0, 0, 0]))
  const uniforms = new Float32Array(40)
  uniforms.set(viewProjection, 0)
  uniforms.set(lightViewProjection, 16)
  uniforms.set([...eye, 1], 32)
  uniforms.set([...light, 1.2], 36)
  return { eye, viewProjection, inverseViewProjection: invert4(viewProjection), lightViewProjection, uniforms }
}
